import { CHUNK_SIZE, CHUNKS_LOADED } from '@blockworld/shared';

// ============================================================
// CHUNK SUBSCRIPTION MANAGER
// Tracks which chunks the client has subscribed to and handles
// subscribe/unsubscribe as the player moves between chunk centers.
// ============================================================

export class ChunkManager {
    private subscribedChunks: Set<string> = new Set();
    private lastChunkCenter: string = '';
    private readonly halfChunks = Math.floor(CHUNKS_LOADED / 2);

    constructor(
        private subscribeFn: (chunks: { cx: number; cy: number }[]) => void,
    ) {}

    /**
     * Immediately request chunks around a position (used for spawn/teleport).
     * Bypasses the center-change check to ensure chunks load immediately.
     */
    requestSpawnChunks(x: number, y: number): void {
        const centerCx = Math.floor(x / CHUNK_SIZE);
        const centerCy = Math.floor(y / CHUNK_SIZE);

        const allNeeded: { cx: number; cy: number }[] = [];
        const neededKeys = new Set<string>();

        for (let cy = centerCy - this.halfChunks; cy <= centerCy + this.halfChunks; cy++) {
            for (let cx = centerCx - this.halfChunks; cx <= centerCx + this.halfChunks; cx++) {
                const key = `${cx},${cy}`;
                neededKeys.add(key);
                allNeeded.push({ cx, cy });
            }
        }

        this.subscribeFn(allNeeded);
        this.subscribedChunks = neededKeys;
        this.lastChunkCenter = `${centerCx},${centerCy}`;
    }

    /**
     * Update subscriptions based on current player position.
     * Only sends a request when the player crosses a chunk center boundary.
     */
    updateChunkSubscriptions(playerX: number, playerY: number): void {
        const centerCx = Math.floor(playerX / CHUNK_SIZE);
        const centerCy = Math.floor(playerY / CHUNK_SIZE);

        const centerKey = `${centerCx},${centerCy}`;
        if (centerKey === this.lastChunkCenter) return;
        this.lastChunkCenter = centerKey;

        const allNeeded: { cx: number; cy: number }[] = [];
        const neededKeys = new Set<string>();

        for (let cy = centerCy - this.halfChunks; cy <= centerCy + this.halfChunks; cy++) {
            for (let cx = centerCx - this.halfChunks; cx <= centerCx + this.halfChunks; cx++) {
                const key = `${cx},${cy}`;
                neededKeys.add(key);
                allNeeded.push({ cx, cy });
            }
        }

        this.subscribeFn(allNeeded);
        this.subscribedChunks = neededKeys;
    }

    /**
     * Reset state (used on teleport or world reset).
     */
    reset(): void {
        this.subscribedChunks.clear();
        this.lastChunkCenter = '';
    }
}
